/**
 * 모바일 월·주 뷰의 "그날 목록" (spec docs/superpowers/specs/2026-09-23-mobile-shell-design.md §4).
 *
 * 날짜 칸을 누르면 그리드 아래 #calAgenda 가 그날로 바뀐다 (예전의 하루 바텀 시트를 대신한다).
 * 목록은 그리드 막대(.cal-bar)의 data-* 에서 만든다 — 막대는 모바일에서 숨겨져 있을 뿐 DOM 에
 * 있으므로 월 이동·실시간 갱신 뒤에도 같은 방법으로 다시 그린다. #calAgenda 는 리전이 아닌 고정
 * 셸이라 새로고침에 갈리지 않고, 그린 내용이 같으면 손대지 않으며, 다시 그릴 때는 포커스가 있던
 * 일정 줄로 포커스를 돌려준다 (M4). 월·주가 아니면(#calGrid 없음) 셸을 숨긴다. 그 밖에: 그리드 가로 스와이프
 * = ‹ ›, FAB = 선택한 날짜로 새 일정, 🔍 검색 펼침, ⋯ 시트에 뷰 툴바와 필터 옮겨 담기(Task 9).
 */
import { registerSheetHooks } from './mobile-shell.js';
import { phoneQuery } from './phone.js';

const MOBILE = phoneQuery;
let selected = '';
let wired = false;

const pad = (n) => String(n).padStart(2, '0');
const localToday = () => { const d = new Date(); return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); };

function barsOn(grid, date) {
    const seen = new Set();
    return Array.from(grid.querySelectorAll('.cal-bar')).filter((bar) => {
        const { start, end, id } = bar.dataset;
        if (!start || !end || start > date || end < date || seen.has(id)) return false;
        seen.add(id);
        return true;
    }).sort((a, b) => {
        const aAll = a.dataset.allday === '1' || !a.dataset.stime;
        const bAll = b.dataset.allday === '1' || !b.dataset.stime;
        if (aAll !== bAll) return aAll ? -1 : 1;
        const at = a.dataset.stime || '';
        const bt = b.dataset.stime || '';
        if (at !== bt) return at < bt ? -1 : 1;
        return (a.dataset.title || '').localeCompare(b.dataset.title || '');
    });
}

// 줄이 보이는 모양을 정하는 막대 속성 — 이것이 같으면 목록을 다시 그리지 않는다.
const ROW_KEYS = ['id', 'title', 'start', 'end', 'stime', 'etime', 'allday', 'done', 'color', 'calname', 'owner', 'series'];
const rowSig = (bar) => ROW_KEYS.map((k) => bar.dataset[k] || '').join('\u001f');

function agendaRow(bar, box) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cal-agenda-row' + (bar.dataset.done === '1' ? ' done' : '');
    btn.dataset.id = bar.dataset.id || '';
    const time = document.createElement('span');
    time.className = 'cal-agenda-time';
    if (bar.dataset.allday === '1' || !bar.dataset.stime) {
        time.textContent = box.dataset.allday || '종일';
    } else {
        const s = document.createElement('b');
        s.textContent = bar.dataset.stime;
        time.appendChild(s);
        if (bar.dataset.etime) {
            const e = document.createElement('span');
            e.textContent = bar.dataset.etime;
            time.appendChild(e);
        }
    }
    const stripe = document.createElement('i');
    stripe.className = 'cal-agenda-stripe cal-' + (bar.dataset.color || 'c1');
    const main = document.createElement('span');
    main.className = 'cal-agenda-main';
    const title = document.createElement('strong');
    title.textContent = bar.dataset.title || '';
    if (bar.dataset.series) {
        const mark = document.createElement('span');
        mark.className = 'cal-series-mark';
        mark.setAttribute('aria-hidden', 'true');
        if (bar.dataset.seriesSummary) mark.title = bar.dataset.seriesSummary; // 규칙 요약 (BA2)
        mark.textContent = '↻';
        title.appendChild(mark);
    }
    const sub = document.createElement('span');
    sub.className = 'cal-agenda-sub';
    sub.textContent = bar.dataset.calname || bar.dataset.owner || '';
    main.append(title, sub);
    btn.append(time, stripe, main);
    btn.addEventListener('click', () => {
        if (!window.openCalendarEvent) return;
        // 같은 모양이면 줄을 다시 만들지 않으므로, 누를 때 지금 그리드의 막대를 찾는다.
        const live = bar.dataset.id && document.querySelector('#calGrid .cal-bar[data-id="' + CSS.escape(bar.dataset.id) + '"]');
        window.openCalendarEvent(live || bar);
    });
    return btn;
}

export function renderCalendarAgenda() {
    const box = document.getElementById('calAgenda');
    if (!box) return;
    const grid = document.getElementById('calGrid');
    box.hidden = !grid; // 뷰는 SPA 로 바뀐다 — 서버가 처음 정한 hidden 을 여기서 맞춘다
    if (!grid) return;
    // 뷰를 바꾸면 새 루트는 옛 루트 자리에 들어간다 — 허브의 표·타임라인(#projHub)은 이 셸보다 뒤에 있어
    // 거기서 월·주로 오면 그리드가 목록 아래에 선다. 목록은 언제나 그리드를 담은 블록 뒤에 둔다.
    if (box.compareDocumentPosition(grid) & Node.DOCUMENT_POSITION_FOLLOWING) {
        let top = grid;
        while (top.parentElement && top.parentElement !== box.parentElement) top = top.parentElement;
        if (top.parentElement === box.parentElement) top.after(box);
    }
    const cells = Array.from(grid.querySelectorAll('.cal-day[data-date]:not(.pad)'));
    if (!cells.length) return;
    const dates = cells.map((c) => c.dataset.date);
    // 셸은 새로 그려지지 않으므로 오래 열어 둔 화면에서 서버의 오늘(data-today)은 낡는다 —
    // 브라우저의 날짜가 더 나중이면 그것을 쓴다.
    const local = localToday();
    const today = box.dataset.today && box.dataset.today >= local ? box.dataset.today : local;
    if (!dates.includes(selected)) selected = dates.includes(today) ? today : dates[0];
    cells.forEach((c) => c.classList.toggle('m-selected', c.dataset.date === selected));
    const cell = grid.querySelector('.cal-day[data-date="' + selected + '"]');
    const weekday = (box.dataset.weekdays || '일,월,화,수,목,금,토').split(',')[new Date(selected + 'T00:00:00').getDay()] || '';
    const holiday = cell && cell.querySelector('.cal-hol') ? cell.querySelector('.cal-hol').textContent.trim() : '';
    const head = document.getElementById('calAgendaHead');
    const headText = selected.slice(5) + ' (' + weekday + ')' + (holiday ? ' · ' + holiday : '');
    if (head.textContent !== headText) head.textContent = headText; // 알림(aria-live)은 날이 바뀔 때만
    const list = document.getElementById('calAgendaList');
    const bars = barsOn(grid, selected);
    const canAdd = !!window.openCalendarCreate && grid.dataset.calId === undefined;
    const sig = selected + '\u001e' + canAdd + '\u001e' + bars.map(rowSig).join('\u001e');
    if (list.dataset.sig === sig) return;
    const focused = list.contains(document.activeElement) ? document.activeElement : null;
    const focusedId = focused && focused.dataset.id;
    list.dataset.sig = sig;
    list.textContent = '';
    if (bars.length) {
        bars.forEach((bar) => list.appendChild(agendaRow(bar, box)));
        if (focused) {
            const again = (focusedId && list.querySelector('.cal-agenda-row[data-id="' + CSS.escape(focusedId) + '"]')) || list.querySelector('.cal-agenda-row');
            if (again) again.focus({ preventScroll: true });
        }
        return;
    }
    const empty = document.createElement('p');
    empty.className = 'cal-agenda-empty';
    empty.textContent = box.dataset.empty || '일정이 없습니다';
    list.appendChild(empty);
    // 개인 캘린더 화면만 (프로젝트 허브의 그리드는 data-cal-id 를 갖고 새 작업 경로가 다르다).
    if (canAdd) {
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'btn btn-secondary btn-sm cal-agenda-add';
        add.textContent = box.dataset.add || '이 날 일정 추가';
        add.addEventListener('click', () => window.openCalendarCreate(selected));
        list.appendChild(add);
        if (focused) add.focus({ preventScroll: true });
    }
}

export function selectCalendarDay(date) {
    selected = date;
    renderCalendarAgenda();
}

// ⋯ 보기 시트 (spec §4.2): 뷰 툴바와 필터 aside 는 DOM 의 서로 다른 곳에 있으므로 시트를 열 때
// 자리표시를 남기고 옮겨 넣고, 닫을 때(또는 화면이 갈릴 때) 되돌린다. 노드 이동이라 리스너가
// 살아 있고, 리전(viewbar · filters)은 위치와 무관하게 swapRegions 가 맞춘다. 시트 안의 칩이
// 자기 팝오버(openExclusive)를 열므로 시트는 비배타로 연다 (mobile-shell.js exclusive:false).
const homes = new Map(); // moved element → placeholder
function moveIntoSheet(sheet) {
    const slot = sheet.querySelector('[data-cal-sheet-slot]');
    if (!slot) return;
    for (const sel of ['.cal-vbar-region', '.cal-filter-aside']) {
        const el = Array.from(document.querySelectorAll(sel)).find((node) => !homes.has(node) && !slot.contains(node));
        if (!el) continue;
        const placeholder = document.createElement('span');
        placeholder.hidden = true;
        placeholder.dataset.calSheetHome = sel;
        el.before(placeholder);
        homes.set(el, placeholder);
        slot.appendChild(el);
    }
}
function moveBack() {
    for (const [el, placeholder] of homes) {
        if (placeholder.isConnected) placeholder.replaceWith(el);
    }
    homes.clear();
}

function toggleSearch(open) {
    document.documentElement.classList.toggle('cal-m-search', open);
    const input = document.querySelector('.cal-search input:not([type="hidden"])');
    if (open && input) input.focus();
}

export function setupCalendarAgenda() {
    window.selectCalendarDay = selectCalendarDay;
    renderCalendarAgenda();
    if (wired) return;
    wired = true;
    ['calendar:refreshed', 'calendar:navigated', 'calendar:root-added', 'calendar:page-swapped'].forEach((name) => {
        document.addEventListener(name, () => renderCalendarAgenda());
    });
    MOBILE.addEventListener('change', () => renderCalendarAgenda());
    registerSheetHooks('calViewSheet', { onOpen: moveIntoSheet, onClose: moveBack, exclusive: false });
    document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cal-m-search]')) { e.preventDefault(); toggleSearch(!document.documentElement.classList.contains('cal-m-search')); return; }
        if (e.target.closest('[data-cal-m-search-close]')) { e.preventDefault(); toggleSearch(false); }
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && document.documentElement.classList.contains('cal-m-search')) toggleSearch(false);
    });
    document.addEventListener('calendar:page-swapped', () => toggleSearch(false));
    document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cal-fab]')) return;
        e.preventDefault();
        if (window.openCalendarCreate) window.openCalendarCreate(selected || localToday());
    });
}
